import { useEffect, useState } from "react";
import { Modal, Row, Col, Card, Upload, Popconfirm, Image, List, Button } from 'antd';
import { UploadOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import { fetchJSON } from "../utils/request";
import { HT3_HOST } from "../config";

// 加密函数
export const simple_encrypt = text => {
	const key = "TPDa*UU8h5%!zS";
	let encrypted = [];
	let keyIndex = 0;
	for (let i = 0; i < text.length; i++) {
		const charCode = text.charCodeAt(i);
		const keyCharCode = key.charCodeAt(keyIndex);
		const encryptedChar = charCode ^ keyCharCode;
		encrypted.push(encryptedChar);
		keyIndex = (keyIndex + 1) % key.length;
	}
	return encrypted.map(byte => byte.toString(16).padStart(2, "0")).join("");
};

// 获取图片列表
const getImageList = async (key, overlist = false) => {
	try {
		let url;
		if (overlist) {
			url = `${HT3_HOST}/oss/list_over_unique_key?key=${key}`;
		} else {
			url = `${HT3_HOST}/oss/list_unique_key?key=${key}`;
		}
		const { errcode, result } = await fetchJSON(url);
		if (errcode === 0) {
			return result
				.map(file => ({
					key: file.key,
					encrypt_key: file.encrypt_key,
					size: file.size,
					status: "done",
					url: file.url,
					last_modified: file.last_modified,
				}))
				.sort((a, b) => {
					const dateA = new Date(a.last_modified);
					const dateB = new Date(b.last_modified);
					return dateA - dateB;
				});
		}
	} catch (error) {
		console.error("获取图片列表失败", error);
	}
	return [];
};

// 删除图片
const deleteImage = async key => {
	try {
		const { errcode } = await fetchJSON(`${HT3_HOST}/oss/delete_unique_key?key=${key}`, {
			method: "GET",
		});
		return errcode === 0;
	} catch (error) {
		console.error("删除图片失败", error);
		return false;
	}
};

// 获取上传签名
const getSignature = async (file, key, onSuccess, onError) => {
	try {
		const { errcode, result } = await fetchJSON(`${HT3_HOST}/oss/signature_unique_key?key=${key}&filename=${file.name}`);
		if (errcode === 0) {
			const { method, host, signed_headers } = result;
			const response = await fetch(host, {
				method,
				headers: signed_headers,
				body: file,
			});
			if (response.ok) {
				onSuccess(response, file);
			} else {
				onError(new Error("图片上传失败"));
			}
		}
	} catch (error) {
		console.error("获取签名失败:", error);
		onError(error);
	}
};

const ImageModal = ({ isOpen, onClose, onSelectImage, osskey, onChange }) => {
  const [fileList, setFileList] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState("");
	const [selectedImages, setSelectedImages] = useState([]);
	const key = simple_encrypt(osskey);

	// 处理删除操作 
	const handleDelete = async file => { 
		const success = await deleteImage(file.encrypt_key); 
		if (success) { 
			const newImages = fileList.filter(item => item.encrypt_key !== file.encrypt_key); 
			if (onChange) { 
				onChange(newImages); 
			} 
			setFileList(newImages); 
		} else { 
			console.error("删除失败"); 
		} 
	}; 

	// 处理上传操作 
	const handleChange = ({ fileList: newFileList }) => { 
		setFileList(newFileList); 
	}; 

	const handleUploadFile = ({ file, onProgress, onSuccess, onError }) => { 
		getSignature( 
			file, 
			key, 
			(response, file) => { 
				getImageList(key).then(newImages => { 
					if (onChange) { 
						onChange(newImages); 
					} 
					setFileList(prevList => { 
						// 找到当前正在上传的文件并移除 
						const index = prevList.findIndex(item => item.status === "uploading"); 
						if (index !== -1) { 
							const newPrevList = [...prevList]; 
							newPrevList.splice(index, 1); 
							// 找出新增元素 
							const newItems = newImages.filter(newItem => !newPrevList.some(prevItem => prevItem.key === newItem.key)); 
							// 将新增元素添加到过滤后的列表末尾 
							return [...newPrevList, ...newItems]; 
						} 

						return prevList; 
					}); 
				}); 
			}, 
			onError 
		); 
	}; 

	const handlePreview = file => { 
		if (!file.url) { 
			return; 
		} 
		setPreviewImage(file.url); 
		setPreviewOpen(true); 
	}; 

	const handleRemove = () => { 
		return false; 
	};

	// 处理图片选择
	const handleImageSelect = (file) => {
		const isSelected = selectedImages.some(item => item.key === file.key);
		if (isSelected) {
			setSelectedImages(selectedImages.filter(item => item.key !== file.key));
		} else {
			setSelectedImages([...selectedImages, file]);
		}
	};

	// 确认选择图片
	const handleConfirm = () => {
		if (selectedImages.length > 0 && onSelectImage) {
			// 如果只选中了一张图片，直接传递URL字符串
			// 如果选中了多张图片，依次插入每一张
			if (selectedImages.length === 1) {
				onSelectImage(selectedImages[0].url);
			} else {
				// 对于多张图片，依次调用onSelectImage插入每一张
				selectedImages.forEach((image, index) => {
					setTimeout(() => {
						onSelectImage(image.url);
					}, index * 100); // 稍微延迟插入，确保按顺序
				});
			}
		}
		onClose();
	};

	// 组件挂载时获取图片列表
	useEffect(() => {
		const loadImages = async () => {
			setIsLoading(true);
			const images = await getImageList(key);
			setFileList(images);
			if (onChange) {
				//作用是回调函数，给外面的组件传递数据
				onChange(images);
			}
			setIsLoading(false);
		};

		if (key) {
			loadImages();
		}
	}, [key]);

  return (
    <Modal
      title="选择图片"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          disabled={selectedImages.length === 0}
          icon={<CheckOutlined />}
        >
          确认选择 ({selectedImages.length})
        </Button>
      ]}
      width={800}
      centered
    >
      <Upload 
			customRequest={handleUploadFile} 
			multiple={true} 
			onRemove={handleRemove} 
			listType="picture-card" 
			fileList={fileList} 
			onPreview={handlePreview} 
			onChange={handleChange} 
			showUploadList={{ 
				showDownloadIcon: true, 
				showRemoveIcon: true, 
				removeIcon: file => { 
					return ( 
						<Popconfirm 
							title="Delete" 
							description="Are you sure you want to delete the file?" 
							onConfirm={() => { 
								handleDelete(file); 
							}} 
							onCancel={() => setFileList([...fileList])} 
							okText="Yes" 
							cancelText="No"> 
							<DeleteOutlined /> 
						</Popconfirm> 
					); 
				}, 
			}}
			itemRender={(originNode, file, fileList, actions) => {
				const isSelected = selectedImages.some(item => item.key === file.key);
				return (
					<div 
						style={{
							position: 'relative',
							width: '104px',
							height: '104px',
							border: isSelected ? '2px solid #1890ff' : 'none',
							borderRadius: '4px',
							cursor: 'pointer',
							overflow: 'hidden',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: '#fafafa'
						}}
						onClick={() => handleImageSelect(file)}
					>
						<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								{originNode}
						</div>
						{isSelected && (
							<div 
								style={{
									position: 'absolute',
									top: 0,
									right: 0,
									background: '#1890ff',
									color: 'white',
									width: '20px',
									height: '20px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									borderRadius: '0 0 0 4px'
								}}
							>
								<CheckOutlined style={{ fontSize: '12px' }} />
							</div>
						)}
					</div>
					);
			}}
		> 
			<div> 
				<UploadOutlined /> 
				<div style={{ marginTop: 8 }}>Select File</div> 
			</div> 
		</Upload> 
		<List loading={isLoading} dataSource={fileList} /> 
		{previewImage && ( 
			<Image 
				wrapperStyle={{ display: "none" }} 
				preview={{ 
					visible: previewOpen, 
					onVisibleChange: visible => setPreviewOpen(visible), 
					afterOpenChange: visible => !visible && setPreviewImage(""), 
				}} 
				src={previewImage} 
			/> 
		)}
    </Modal>
  );
};

export default ImageModal;